/* Rubin Extensions — shared, store-wide Judge.me review gallery
   ===========================================================================
   Renders ONE identical gallery under every product from Judge.me's own
   All-Reviews endpoint (cdn.judge.me/reviews/all_reviews_js_based) using the
   same parameter vocabulary Judge.me's All Reviews widget uses:
   url / shop_domain / platform / page / review_type / filter_rating / sort_by.

   No product filter is ever sent, so the set, the order and the counter are
   the same on every product page. Nothing is written to Judge.me; reviews keep
   their original product assignment and nothing is duplicated or hidden there.

   Order (across the COMPLETE collection, not just the first page):
     1. every published review WITH pictures, ratings 5/4/3, newest first,
        merged across the rating streams — products mixed, no product sections;
     2. then text-only reviews, ratings 5/4/3, newest first, product reviews
        and shop reviews merged.
   Reviews whose only media is video sort with the text-only group, because
   Judge.me's with_pictures set contains picture reviews only.

   "Alle Bewertungen" switches the same grid to the complete set (all ratings).
   Ratings 1-2 are simply not requested for the selection; they stay published.

   Dates are never rendered. One review is always one card, however many
   photos it has — the rest open in the lightbox.                           */
(function () {
  'use strict';

  var ENDPOINT = 'https://cdn.judge.me/reviews/all_reviews_js_based';
  var RATINGS = [5, 4, 3];
  var TILE_W = 800;   /* uniform tile image width  */
  var FULL_W = 1600;  /* lightbox image width      */

  function qs(root, sel) { return root.querySelector(sel); }
  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function setWidth(url, w) {
    if (!url) return url;
    return url.indexOf('width=') > -1 ? url.replace(/width=\d+/, 'width=' + w) : url;
  }

  /* ------------------------------------------------------------- fetching */

  function Stream(cfg, params) {
    this.cfg = cfg;
    this.params = params;
    this.page = 0;
    this.buf = [];
    this.done = false;
  }
  Stream.prototype.fill = function () {
    var self = this;
    if (self.done || self.buf.length) return Promise.resolve();
    self.page += 1;
    var p = new URLSearchParams({
      url: self.cfg.shopDomain,
      shop_domain: self.cfg.shopDomain,
      platform: 'shopify',
      page: String(self.page)
    });
    Object.keys(self.params).forEach(function (k) {
      if (self.params[k] != null) p.set(k, String(self.params[k]));
    });
    return fetch(ENDPOINT + '?' + p.toString(), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('judge.me ' + r.status); return r.json(); })
      .then(function (json) {
        var list = parseReviews(json && json.html);
        if (!list.length) { self.done = true; return; }
        self.buf = self.buf.concat(list);
      });
  };
  Stream.prototype.peek = function () { return this.buf.length ? this.buf[0] : null; };
  Stream.prototype.drop = function () { return this.buf.shift(); };

  /* Parse Judge.me's review HTML fragment into plain objects. */
  function parseReviews(html) {
    if (!html) return [];
    var box = document.createElement('div');
    box.innerHTML = html;
    return Array.prototype.map.call(box.querySelectorAll('.jdgm-rev'), function (node) {
      var ratingEl = node.querySelector('.jdgm-rev__rating');
      var bodyEl = node.querySelector('.jdgm-rev__body');
      var pics = Array.prototype.map.call(
        node.querySelectorAll('.jdgm-rev__pics .jdgm-rev__pic-link'),
        function (a) {
          var img = a.querySelector('img');
          var full = a.getAttribute('href') || (img && img.getAttribute('data-src'));
          var thumb = (img && img.getAttribute('data-src')) || full;
          return { thumb: setWidth(thumb, TILE_W), full: setWidth(full, FULL_W) };
        }
      ).filter(function (p) { return p.thumb; });
      var stampEl = node.querySelector('.jdgm-rev__timestamp');
      return {
        id: node.getAttribute('data-review-id'),
        rating: Number(ratingEl && ratingEl.getAttribute('data-score')) || 0,
        verified: node.getAttribute('data-verified-buyer') === 'true',
        name: (node.querySelector('.jdgm-rev__author') || {}).textContent || '',
        title: ((node.querySelector('.jdgm-rev__title') || {}).textContent || '').trim(),
        bodyHtml: bodyEl ? bodyEl.innerHTML : '',
        bodyText: bodyEl ? (bodyEl.textContent || '').trim() : '',
        product: (node.getAttribute('data-product-title') || '').trim(),
        pics: pics,
        /* read only to order the set — never rendered */
        ts: stampEl ? (Date.parse(stampEl.getAttribute('data-content')) || 0) : 0
      };
    }).filter(function (r) { return r.id; });
  }

  /* Next review across a set of streams, newest first, skipping what skipFn
     rejects. Each round either yields a review or advances a stream's page;
     a stream with no further results is marked done, so this terminates. */
  function pickNext(streams, skipFn) {
    function attempt() {
      var live = streams.filter(function (s) { return !s.done || s.buf.length; });
      if (!live.length) return Promise.resolve(null);
      return Promise.all(live.map(function (s) { return s.fill(); })).then(function () {
        var best = null, bestStream = null, pending = false;
        streams.forEach(function (s) {
          var head;
          while ((head = s.peek()) && skipFn(head)) s.drop();
          head = s.peek();
          if (head) {
            if (!best || head.ts > best.ts) { best = head; bestStream = s; }
          } else if (!s.done) {
            pending = true;
          }
        });
        if (best) { bestStream.drop(); return best; }
        return pending ? attempt() : null;
      });
    }
    return attempt();
  }

  /* ------------------------------------------------------------- gallery  */

  function Gallery(root) {
    var d = root.dataset, self = this;
    this.root = root;
    this.cfg = {
      shopDomain: d.shopDomain,
      batch: Math.max(3, Number(d.batch || 12)),
      selTotal: Number(d.selTotal || 0),
      allTotal: Number(d.allTotal || 0),
      labels: {
        counter: d.labelCounter || '[shown] / [total]',
        counterAll: d.labelCounterAll || d.labelCounter || '[shown] / [total]',
        verified: d.labelVerified || 'Verified',
        more: d.labelMoreText || 'Read more',
        less: d.labelLessText || 'Read less',
        loading: d.labelLoading || '',
        error: d.labelError || '',
        empty: d.labelEmpty || '',
        photos: d.labelPhotos || '+[n]'
      }
    };
    this.grid = qs(root, '[data-rx-grid]');
    this.moreBtn = qs(root, '[data-rx-more]');
    this.status = qs(root, '[data-rx-status]');
    this.sentinel = qs(root, '[data-rx-sentinel]');
    this.counter = document.querySelector('[data-rx-counter]');

    this.mode = 'selection';
    this.reset();

    if (this.moreBtn) this.moreBtn.addEventListener('click', function () { self.load(); });

    /* Auto-load while scrolling; the button stays as the fallback. */
    if ('IntersectionObserver' in window && this.sentinel) {
      this.io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) self.load();
      }, { rootMargin: '400px 0px' });
      this.io.observe(this.sentinel);
    }
  }

  Gallery.prototype.reset = function () {
    var cfg = this.cfg, selection = this.mode === 'selection';
    this.seen = {};
    this.shown = 0;
    this.busy = false;
    this.exhausted = false;
    if (this.grid) this.grid.innerHTML = '';

    /* Phase 1 — reviews with pictures. Judge.me's with_pictures set holds
       picture reviews only, so this is exact rather than inferred. */
    this.photo = selection
      ? RATINGS.map(function (r) {
          return new Stream(cfg, { review_type: 'product-reviews', sort_by: 'with_pictures', filter_rating: r });
        })
      : [new Stream(cfg, { review_type: 'product-reviews', sort_by: 'with_pictures' })];

    /* Phase 2 — everything else, product reviews and shop reviews merged. */
    var types = ['product-reviews', 'shop-reviews'];
    this.text = [];
    types.forEach(function (t) {
      if (selection) {
        RATINGS.forEach(function (r) {
          this.text.push(new Stream(cfg, { review_type: t, filter_rating: r }));
        }, this);
      } else {
        this.text.push(new Stream(cfg, { review_type: t }));
      }
    }, this);
  };

  Gallery.prototype.next = function () {
    var self = this;
    return pickNext(self.photo, function (r) { return !!self.seen[r.id]; }).then(function (r) {
      if (r) { self.seen[r.id] = true; return r; }
      return pickNext(self.text, function (x) {
        return !!self.seen[x.id] || x.pics.length > 0;
      }).then(function (t) {
        if (t) self.seen[t.id] = true;
        return t;
      });
    });
  };

  Gallery.prototype.take = function (n) {
    var self = this, out = [];
    function step() {
      if (out.length >= n) return Promise.resolve(out);
      return self.next().then(function (r) {
        if (!r) return out;
        out.push(r);
        return step();
      });
    }
    return step();
  };

  Gallery.prototype.load = function () {
    var self = this;
    if (self.busy || self.exhausted) return;
    self.busy = true;
    if (self.moreBtn) self.moreBtn.disabled = true;
    if (self.status) { self.status.textContent = self.cfg.labels.loading; self.status.hidden = false; }

    self.take(self.cfg.batch).then(function (items) {
      items.forEach(function (r) { self.grid.appendChild(self.card(r)); });
      self.shown += items.length;
      self.exhausted = items.length < self.cfg.batch;
      self.paint();
    }).catch(function (err) {
      if (window.console) console.warn('[rubin-judgeme] gallery failed', err);
      if (self.status) { self.status.textContent = self.cfg.labels.error; self.status.hidden = false; }
      if (self.moreBtn) self.moreBtn.hidden = true;
      self.exhausted = true;
    }).then(function () { self.busy = false; });
  };

  Gallery.prototype.paint = function () {
    var selection = this.mode === 'selection';
    var known = selection ? this.cfg.selTotal : this.cfg.allTotal;
    var total = this.exhausted ? this.shown : Math.max(known, this.shown);
    if (this.counter) {
      var tpl = selection ? this.cfg.labels.counter : this.cfg.labels.counterAll;
      this.counter.textContent = tpl
        .replace('[shown]', String(this.shown))
        .replace('[total]', String(total));
    }
    if (this.moreBtn) { this.moreBtn.hidden = this.exhausted; this.moreBtn.disabled = false; }
    if (this.status) {
      if (this.exhausted && this.shown === 0) {
        this.status.textContent = this.cfg.labels.empty;
        this.status.hidden = false;
      } else {
        this.status.hidden = true;
      }
    }
  };

  Gallery.prototype.setMode = function (mode) {
    if (this.mode === mode) return;
    this.mode = mode;
    this.reset();
    this.paint();
    this.load();
  };

  var CHECK = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">'
    + '<circle cx="10" cy="10" r="10" fill="#111111"></circle>'
    + '<path d="M5.6 10.3l2.9 2.9 5.9-6.1" fill="none" stroke="#ffffff" stroke-width="2"'
    + ' stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  Gallery.prototype.card = function (r) {
    var L = this.cfg.labels;
    var art = el('article', 'rx-rev');
    art.setAttribute('data-review-id', r.id);
    art.setAttribute('data-rating', String(r.rating));

    if (r.pics.length) {
      var media = el('button', 'rx-rev__media');
      media.type = 'button';
      media.setAttribute('aria-label', 'Enlarge customer photo');
      var img = el('img');
      img.src = r.pics[0].thumb;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.alt = '';
      media.appendChild(img);
      if (r.pics.length > 1) {
        media.appendChild(el('span', 'rx-rev__count', L.photos.replace('[n]', String(r.pics.length - 1))));
      }
      media.addEventListener('click', function () {
        openLightbox(r.pics.map(function (p) { return p.full; }), 0);
      });
      art.appendChild(media);
    }

    var body = el('div', 'rx-rev__body');

    var who = el('div', 'rx-rev__who');
    who.appendChild(el('p', 'rx-rev__name', r.name.trim()));
    if (r.verified) {
      var v = el('span', 'rx-rev__verified');
      v.innerHTML = CHECK;
      v.appendChild(document.createTextNode(L.verified));
      who.appendChild(v);
    }
    body.appendChild(who);

    var stars = el('span', 'rx-stars');
    stars.style.setProperty('--rx-fill', (r.rating * 20) + '%');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', r.rating + ' out of 5 stars');
    stars.appendChild(el('span', 'rx-stars__on'));
    body.appendChild(stars);

    if (r.title) body.appendChild(el('h3', 'rx-rev__title', r.title));

    var text = el('div', 'rx-rev__text');
    text.appendChild(safeHtml(r.bodyHtml || ('<p>' + r.bodyText + '</p>')));
    body.appendChild(text);

    var toggle = el('button', 'rx-rev__toggle', L.more);
    toggle.type = 'button';
    toggle.hidden = true;
    toggle.addEventListener('click', function () {
      var open = art.classList.toggle('rx-rev--open');
      toggle.textContent = open ? L.less : L.more;
    });
    body.appendChild(toggle);

    if (r.product) body.appendChild(el('p', 'rx-rev__product', r.product));

    art.appendChild(body);

    /* Reveal "Mehr lesen" only when the text is actually clamped. */
    requestAnimationFrame(function () {
      if (text.scrollHeight > text.clientHeight + 2) toggle.hidden = false;
    });
    return art;
  };

  /* Judge.me's own markup, but strip anything active before injecting it. */
  function safeHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html || '';
    var bad = tpl.content.querySelectorAll('script,iframe,object,embed,style,link,form');
    for (var i = 0; i < bad.length; i++) bad[i].parentNode.removeChild(bad[i]);
    var all = tpl.content.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      var attrs = all[j].attributes;
      for (var k = attrs.length - 1; k >= 0; k--) {
        var n = attrs[k].name.toLowerCase();
        if (n.indexOf('on') === 0 || (n === 'href' && /^\s*javascript:/i.test(attrs[k].value))) {
          all[j].removeAttribute(attrs[k].name);
        }
      }
    }
    return tpl.content;
  }

  /* ------------------------------------------------------------ lightbox  */

  var lb = null;
  function ensureLightbox() {
    if (lb) return lb;
    lb = el('div', 'rx-lightbox');
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.hidden = true;
    lb.innerHTML =
      '<div class="rx-lightbox__backdrop" data-lb-close></div>' +
      '<figure class="rx-lightbox__figure">' +
      '<img class="rx-lightbox__img" alt="Customer photo">' +
      '<button type="button" class="rx-lightbox__nav rx-lightbox__nav--prev" data-lb-prev aria-label="Previous photo">&#8249;</button>' +
      '<button type="button" class="rx-lightbox__nav rx-lightbox__nav--next" data-lb-next aria-label="Next photo">&#8250;</button>' +
      '<button type="button" class="rx-lightbox__close" data-lb-close aria-label="Close">&times;</button>' +
      '</figure>';
    document.body.appendChild(lb);
    lb.addEventListener('click', function (e) {
      if (e.target.hasAttribute('data-lb-close')) closeLightbox();
      else if (e.target.hasAttribute('data-lb-prev')) showLightbox(lb._i - 1);
      else if (e.target.hasAttribute('data-lb-next')) showLightbox(lb._i + 1);
    });
    document.addEventListener('keydown', function (e) {
      if (!lb || lb.hidden) return;
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showLightbox(lb._i - 1);
      else if (e.key === 'ArrowRight') showLightbox(lb._i + 1);
    });
    return lb;
  }
  function showLightbox(i) {
    var n = lb._urls.length;
    lb._i = ((i % n) + n) % n;
    qs(lb, '.rx-lightbox__img').src = lb._urls[lb._i];
    lb.classList.toggle('rx-lightbox--single', n < 2);
  }
  function openLightbox(urls, idx) {
    ensureLightbox();
    lb._urls = (urls || []).filter(Boolean);
    if (!lb._urls.length) return;
    showLightbox(idx || 0);
    lb.hidden = false;
    document.documentElement.classList.add('rx-lightbox-open');
    qs(lb, '.rx-lightbox__close').focus();
  }
  function closeLightbox() {
    if (!lb) return;
    lb.hidden = true;
    document.documentElement.classList.remove('rx-lightbox-open');
  }

  /* ---------------------------------------------------------------- init  */

  function initSection(section) {
    if (section.getAttribute('data-rx-init')) return;
    section.setAttribute('data-rx-init', '1');

    var root = qs(section, '[data-rx-gallery]');
    var widget = qs(section, '[data-rx-product-widget]');
    var gallery = root ? new Gallery(root) : null;

    var toggleAll = qs(section, '[data-rx-toggle-all]');
    if (toggleAll && gallery) {
      toggleAll.setAttribute('data-orig', toggleAll.textContent.trim());
      toggleAll.addEventListener('click', function () {
        var toAll = gallery.mode === 'selection';
        gallery.setMode(toAll ? 'all' : 'selection');
        toggleAll.setAttribute('aria-pressed', toAll ? 'true' : 'false');
        toggleAll.textContent = toAll
          ? (toggleAll.getAttribute('data-alt') || 'Selection only (3–5 stars)')
          : (toggleAll.getAttribute('data-orig') || 'All reviews');
      });
    }

    /* "Bewertung schreiben" opens the official per-product widget, which is
       what keeps a new review attached to THIS product. */
    function showProductWidget(openForm) {
      if (!widget) return;
      if (root) root.hidden = true;
      var head = qs(section, '.rx-revs__head');
      if (head) head.hidden = true;
      widget.classList.remove('rubin-judgeme-reviews__widget--collapsed');
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (openForm) {
          var btns = widget.querySelectorAll('button, a');
          var w = Array.prototype.filter.call(btns, function (b) {
            return /bewertung schreiben|write a review/i.test(b.textContent || '');
          })[0];
          if (w) { w.click(); openForm = false; }
        }
        if (tries > 24 || !openForm) clearInterval(timer);
      }, 250);
      try { widget.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { widget.scrollIntoView(); }
    }
    function hideProductWidget() {
      if (widget) widget.classList.add('rubin-judgeme-reviews__widget--collapsed');
      if (root) root.hidden = false;
      var head = qs(section, '.rx-revs__head');
      if (head) head.hidden = false;
      try { section.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { section.scrollIntoView(); }
    }

    section.addEventListener('click', function (e) {
      var t = e.target.closest('[data-rx-write],[data-rx-back]');
      if (!t) return;
      e.preventDefault();
      if (t.hasAttribute('data-rx-back')) hideProductWidget();
      else showProductWidget(true);
    });

    /* First load happens immediately — this theme scrolls with a transformed
       wrapper, so an observer-only gate is not reliable for the first paint. */
    if (gallery) gallery.load();
  }

  function init() {
    var sections = document.querySelectorAll('.rubin-judgeme-reviews');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
